import { useState } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  Search, FileText, DollarSign, Award, Building2, ExternalLink,
} from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import {
  useResumoContratos, useContratosLista, useRankingOrgaos, useRankingFornecedores,
  type ContratosFiltros,
} from '@/hooks/useMercadoContratos';
import { DS_BRAND, chartSx, num } from './charts/chartTheme';
import { Kpi, Painel, Vazio } from './ui';
import { brl, nInt, dataBR, pncpBusca } from './format';

const onlyDigits = (s: string) => s.replace(/\D/g, '');
const curto = (s: string | null, n = 24) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '—');

export function ContratosMercado({ portal }: { portal?: boolean }) {
  const [busca, setBusca] = useState('');
  const [periodoMeses, setPeriodoMeses] = useState(12);
  const [uf, setUf] = useState('');
  const [q, setQ] = useState<ContratosFiltros>({ periodoMeses: 12 });

  function pesquisar() {
    const termo = busca.trim();
    const digits = onlyDigits(termo);
    const f: ContratosFiltros = { periodoMeses: periodoMeses || undefined };
    if (uf.trim()) f.uf = uf.trim().toUpperCase();
    if (digits.length === 14) f.cnpj = digits;
    else if (termo) f.q = termo;
    setQ(f);
  }

  const resumo = useResumoContratos(q, portal);
  const lista = useContratosLista({ ...q, limite: 100 }, portal);
  const rankBase = { uf: q.uf, periodoMeses: q.periodoMeses, q: q.q, cnpj: q.cnpj };
  const orgaos = useRankingOrgaos({ ...rankBase, limite: 8 }, portal);
  const forns = useRankingFornecedores({ ...rankBase, limite: 8 }, portal);

  const r = resumo.data;
  const topForn = forns.data?.[0];
  const topOrg = orgaos.data?.[0];
  const inputCls = 'rounded-md border border-ds-border bg-ds-surface px-2 py-1.5 text-sm text-ds-text-primary';

  return (
    <div className="space-y-5">
      {/* busca */}
      <form
        className="flex flex-wrap items-stretch gap-2"
        onSubmit={(e) => { e.preventDefault(); pesquisar(); }}
      >
        <div className="flex min-w-[260px] flex-[2] items-center gap-2 rounded-md border border-ds-border bg-ds-surface px-3">
          <Search size={16} className="text-ds-text-secondary" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Digite um CNPJ (14 dígitos) ou um termo para filtrar os contratos…"
            className="w-full bg-transparent py-2 text-sm text-ds-text-primary outline-none"
          />
        </div>
        <input value={uf} onChange={(e) => setUf(e.target.value)} placeholder="UF" maxLength={2} className={`${inputCls} w-20`} />
        <select value={periodoMeses} onChange={(e) => setPeriodoMeses(Number(e.target.value))} className={inputCls}>
          {[12, 24, 36, 60].map((m) => <option key={m} value={m}>Últimos {m} meses</option>)}
        </select>
        <button type="submit" className="rounded-md bg-ds-brand-solid px-4 py-2 text-sm font-semibold text-ds-on-brand">
          Buscar
        </button>
      </form>

      {resumo.isError ? <Vazio texto="Falha ao carregar os contratos." /> : (
        <>
          {/* KPIs */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={<FileText size={13} />} titulo="Total de contratos" valor={nInt(r?.total_contratos)} />
            <Kpi icon={<DollarSign size={13} />} titulo="Valor movimentado" valor={brl(r?.valor_total)} />
            <Kpi icon={<Award size={13} />} titulo="Empresas vencedoras" valor={nInt(r?.empresas_vencedoras)} />
            <Kpi icon={<Building2 size={13} />} titulo="Órgãos compradores" valor={nInt(r?.orgaos_compradores)} />
          </div>

          {/* cards secundários */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Kpi titulo="Valor médio por contrato" valor={brl(r?.valor_medio)} />
            <Kpi titulo="Maior fornecedor" valor={curto(topForn?.fornecedor_nome ?? null, 28)} detalhe={topForn ? brl(topForn.valor_total) : undefined} />
            <Kpi titulo="Maior comprador" valor={curto(topOrg?.orgao_razao_social ?? null, 28)} detalhe={topOrg ? brl(topOrg.valor_total) : undefined} />
          </div>

          {/* gráficos de barra */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Painel titulo="🏅 Maiores Fornecedores">
              {(forns.data ?? []).length === 0 ? <Vazio texto="Sem fornecedores no recorte." /> : (
                <BarChart
                  layout="horizontal"
                  colors={[DS_BRAND]}
                  height={Math.max(200, (forns.data?.length ?? 0) * 34 + 40)}
                  yAxis={[{ scaleType: 'band', data: (forns.data ?? []).map((f) => f.fornecedor_nome || f.fornecedor_ni), valueFormatter: (v: string) => curto(v, 20) }]}
                  series={[{ data: (forns.data ?? []).map((f) => num(f.valor_total)), valueFormatter: (v) => brl(v ?? 0) }]}
                  margin={{ left: 130, right: 16, top: 8, bottom: 24 }}
                  sx={chartSx}
                />
              )}
            </Painel>
            <Painel titulo="🏛️ Maiores Compradores">
              {(orgaos.data ?? []).length === 0 ? <Vazio texto="Sem órgãos no recorte." /> : (
                <BarChart
                  layout="horizontal"
                  colors={[DS_BRAND]}
                  height={Math.max(200, (orgaos.data?.length ?? 0) * 34 + 40)}
                  yAxis={[{ scaleType: 'band', data: (orgaos.data ?? []).map((o) => o.orgao_razao_social || o.orgao_cnpj), valueFormatter: (v: string) => curto(v, 20) }]}
                  series={[{ data: (orgaos.data ?? []).map((o) => num(o.valor_total)), valueFormatter: (v) => brl(v ?? 0) }]}
                  margin={{ left: 130, right: 16, top: 8, bottom: 24 }}
                  sx={chartSx}
                />
              )}
            </Painel>
          </div>

          {/* tabela */}
          <Painel titulo="📄 Contratos">
            {lista.isLoading ? <Vazio texto="Carregando…" />
              : (lista.data ?? []).length === 0 ? <Vazio texto="Nenhum contrato no recorte." />
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead>Órgão</TableHead>
                        <TableHead>Local</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-right">Assinatura</TableHead>
                        <TableHead className="text-right">PNCP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(lista.data ?? []).map((c) => (
                        <TableRow key={c.numero_controle_pncp}>
                          <TableCell className="max-w-[220px]">
                            <div className="truncate text-ds-text-primary" title={c.fornecedor_nome || ''}>{c.fornecedor_nome || '—'}</div>
                            <div className="text-[11px] text-ds-text-secondary">{c.fornecedor_ni || ''}</div>
                          </TableCell>
                          <TableCell className="max-w-[220px] truncate" title={c.orgao_razao_social || ''}>{c.orgao_razao_social || '—'}</TableCell>
                          <TableCell className="whitespace-nowrap">{[c.unidade_municipio, c.unidade_uf].filter(Boolean).join('/') || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-ds-success">{brl(c.valor_global)}</TableCell>
                          <TableCell className="text-right tabular-nums">{dataBR(c.data_assinatura)}</TableCell>
                          <TableCell className="text-right">
                            <a href={pncpBusca(c.numero_controle_pncp)} target="_blank" rel="noreferrer" className="inline-flex text-ds-brand hover:underline" title="Abrir no PNCP">
                              <ExternalLink size={15} />
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
          </Painel>
        </>
      )}
    </div>
  );
}

export default ContratosMercado;
